import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import {
  DatePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  CalendarToday as CalendarIcon,
  DateRange as DateRangeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DateRange, DateRangePickerProps, PresetOption } from '../../types';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subDays } from 'date-fns';
import { validateDateRange, calculateDateRangeDays } from './validation';

const DEFAULT_PRESETS: PresetOption[] = [
  {
    label: 'This month',
    value: 'this_month',
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date())
  },
  {
    label: 'Last 30 days',
    value: 'last_30_days',
    startDate: subDays(new Date(), 30),
    endDate: new Date()
  },
  {
    label: 'Last month',
    value: 'last_month',
    startDate: startOfMonth(subMonths(new Date(), 1)),
    endDate: endOfMonth(subMonths(new Date(), 1))
  },
  {
    label: 'Last 3 months',
    value: 'last_3_months',
    startDate: subMonths(new Date(), 3),
    endDate: new Date()
  },
  {
    label: 'Last 6 months',
    value: 'last_6_months',
    startDate: subMonths(new Date(), 6),
    endDate: new Date()
  },
  {
    label: 'Year to date',
    value: 'year_to_date',
    startDate: startOfYear(new Date()),
    endDate: new Date()
  },
  {
    label: 'Last year',
    value: 'last_year',
    startDate: startOfYear(subMonths(new Date(), 12)),
    endDate: endOfYear(subMonths(new Date(), 12))
  }
];

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  dateRange,
  onDateRangeChange,
  presetOptions = DEFAULT_PRESETS,
  maxDate, // Remove default restriction to allow future dates
  minDate,
  loading = false,
  error
}) => {
  const [selectedPreset, setSelectedPreset] = useState<string>(dateRange.preset || 'this_month');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(dateRange.startDate);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(dateRange.endDate);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Update local state when external dateRange changes
  useEffect(() => {
    setSelectedPreset(dateRange.preset || 'this_month');
    setCustomStartDate(dateRange.startDate);
    setCustomEndDate(dateRange.endDate);
  }, [dateRange]);

  // Validate current date range
  useEffect(() => {
    if (customStartDate && customEndDate) {
      const validation = validateDateRange(
        { startDate: customStartDate, endDate: customEndDate },
        minDate
        // Don't pass maxDate to allow future dates
      );
      setValidationErrors(validation.errors);
    } else {
      setValidationErrors([]);
    }
  }, [customStartDate, customEndDate, minDate]);

  const handlePresetChange = (event: SelectChangeEvent<string>) => {
    const presetValue = event.target.value;
    setSelectedPreset(presetValue);

    if (presetValue === 'custom') {
      // Keep current custom dates
      return;
    }

    const preset = presetOptions.find(p => p.value === presetValue);
    if (preset) {
      const newRange: DateRange = {
        startDate: preset.startDate,
        endDate: preset.endDate,
        preset: preset.value
      };

      // Don't validate against maxDate for future dates - allow them
      const validation = validateDateRange(newRange, minDate);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        return;
      }

      setCustomStartDate(preset.startDate);
      setCustomEndDate(preset.endDate);
      setValidationErrors([]); // Clear any previous errors
      
      console.log('DateRangePicker: Preset changed to', presetValue, {
        startDate: preset.startDate.toISOString().split('T')[0],
        endDate: preset.endDate.toISOString().split('T')[0]
      });
      
      onDateRangeChange(newRange);
    }
  };

  const handleCustomDateChange = (field: 'start' | 'end', date: Date | null) => {
    if (!date) return;

    let newStartDate = field === 'start' ? date : customStartDate;
    let newEndDate = field === 'end' ? date : customEndDate;

    if (field === 'start') {
      setCustomStartDate(date);
    } else {
      setCustomEndDate(date);
    }

    // Only validate and update if both dates are set
    if (newStartDate && newEndDate) {
      const validation = validateDateRange(
        { startDate: newStartDate, endDate: newEndDate },
        minDate
        // Don't pass maxDate to allow future dates
      );

      if (validation.isValid) {
        setSelectedPreset('custom');
        const newRange: DateRange = {
          startDate: newStartDate,
          endDate: newEndDate,
          preset: 'custom'
        };
        onDateRangeChange(newRange);
      }
    }
  };

  const handleApplyCustomRange = () => {
    if (!customStartDate || !customEndDate) {
      setValidationErrors(['Both start and end dates are required']);
      return;
    }

    const validation = validateDateRange(
      { startDate: customStartDate, endDate: customEndDate },
      minDate
      // Don't pass maxDate to allow future dates
    );

    if (validation.isValid) {
      setSelectedPreset('custom');
      const newRange: DateRange = {
        startDate: customStartDate,
        endDate: customEndDate,
        preset: 'custom'
      };
      onDateRangeChange(newRange);
    }
  };

  const formatDateRange = (startDate: Date, endDate: Date): string => {
    return `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`;
  };

  const hasValidationErrors = validationErrors.length > 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography>Loading date options...</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={1} sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {/* Preset Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="date-preset-label">Date Range</InputLabel>
                <Select
                  labelId="date-preset-label"
                  id="date-preset-select"
                  value={selectedPreset}
                  label="Date Range"
                  onChange={handlePresetChange}
                  startAdornment={<DateRangeIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  {presetOptions.map((preset) => (
                    <MenuItem key={preset.value} value={preset.value}>
                      <Box>
                        <Typography variant="body2">{preset.label}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatDateRange(preset.startDate, preset.endDate)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                  <MenuItem value="custom">
                    <Box>
                      <Typography variant="body2">Custom Range</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Select your own dates
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Custom Date Pickers */}
            {selectedPreset === 'custom' && (
              <>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Start Date"
                    value={customStartDate}
                    onChange={(date) => handleCustomDateChange('start', date)}
                    maxDate={customEndDate}
                    minDate={minDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: hasValidationErrors,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="End Date"
                    value={customEndDate}
                    onChange={(date) => handleCustomDateChange('end', date)}
                    minDate={customStartDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: hasValidationErrors,
                      }
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {/* Validation Errors */}
          {hasValidationErrors && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Date Range Summary */}
          {!hasValidationErrors && customStartDate && customEndDate && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={<CalendarIcon />}
                  label={formatDateRange(customStartDate, customEndDate)}
                  variant="outlined"
                  color="primary"
                />
                <Chip
                  label={`${calculateDateRangeDays({ startDate: customStartDate, endDate: customEndDate })} days`}
                  variant="outlined"
                  size="small"
                />
                {selectedPreset !== 'custom' && (
                  <Chip
                    label={presetOptions.find(p => p.value === selectedPreset)?.label || selectedPreset}
                    variant="filled"
                    size="small"
                    color="secondary"
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Apply Button for Custom Range */}
          {selectedPreset === 'custom' && customStartDate && customEndDate && hasValidationErrors && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={handleApplyCustomRange}
                disabled={hasValidationErrors}
              >
                Apply Date Range
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};